import { useEffect, useState } from "react";
import { Crown, Target, Rocket, Quote, MapPin, MessageCircle, TrendingUp, Globe2, ShieldCheck, Pencil, User } from "lucide-react";
import { useSite } from "../lib/site";
import { imgUrl } from "../lib/api";
import BackButton from "../components/BackButton";
import OwnerEditor from "../components/OwnerEditor";
import EditPencil from "../components/EditPencil";

const WHY = [[TrendingUp, "Early-stage growth", "Get in at the ground floor of a fast-scaling marketplace."], [Globe2, "Pan-India expansion", "Built for all 36 States & UTs from day one."], [Rocket, "Booming used-car market", "India's pre-owned car market is growing faster than new cars."], [ShieldCheck, "Transparent & founder-led", "Direct access to the founder, clear numbers, honest updates."]];

export default function Invest() {
  const { owner, isAdmin } = useSite();
  const [edit, setEdit] = useState(false);
  useEffect(() => { document.title = "Invest With Us | CarSaleMarket.com"; }, []);
  if (!owner) return <div className="pt-40 text-center text-gray-500">Loading...</div>;
  const wa = `https://wa.me/${owner.whatsapp}?text=${encodeURIComponent("INVEST")}`;

  return (
    <div className="keep-dark relative min-h-screen overflow-hidden bg-[#071330]" data-testid="invest-page">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(212,175,55,0.18),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(30,64,175,0.35),transparent_60%)]" />
      <div className="absolute inset-0 grain opacity-60" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16">
        <div className="flex items-center justify-between gap-3 mb-8">
          <BackButton testid="invest-back" />
          {isAdmin && <button onClick={() => setEdit(true)} className="btn-gold !py-1.5 !px-3 text-xs" data-testid="owner-edit-btn"><Pencil className="w-3.5 h-3.5" />Edit owner profile</button>}
        </div>

        <div className="text-center max-w-3xl mx-auto rise">
          <p className="eyebrow !text-[11px] flex items-center justify-center gap-2"><Crown className="w-3.5 h-3.5" />Investor relations<EditPencil section="invest" testid="invest-section-edit" /></p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.05]">Invest in <span className="gold-text italic">CarSaleMarket.com</span></h1>
          <p className="text-blue-100/80 mt-4 text-sm sm:text-base">Be part of India's next big car platform — connecting buyers directly with private owners and trusted dealers across all 36 States & UTs.</p>
        </div>

        <div className="mt-14 grid lg:grid-cols-[380px_1fr] gap-8 items-stretch">
          <div className="relative rounded-3xl p-[1.5px] rise" style={{ background: "linear-gradient(140deg,#F5E08F,#B8860B 40%,#1e3a8a 70%,#D4AF37)", animationDelay: "120ms" }} data-testid="owner-card">
            <div className="rounded-3xl bg-[#0a1a3f]/95 backdrop-blur-xl p-7 h-full flex flex-col items-center text-center">
              <div className="relative">
                <div className="absolute -inset-3 rounded-full bg-[#D4AF37]/20 blur-xl" />
                <div className="relative w-40 h-40 rounded-full overflow-hidden border-[3px] border-[#D4AF37] bg-[#132650] flex items-center justify-center shadow-2xl">
                  {owner.photo_url ? <img src={imgUrl(owner.photo_url)} alt={owner.name} className="w-full h-full object-cover" data-testid="owner-photo-img" /> : <User className="w-16 h-16 text-[#D4AF37]" />}
                </div>
                <span className="absolute bottom-1 right-1 w-10 h-10 rounded-full flex items-center justify-center border-2 border-[#0a1a3f]" style={{ background: "var(--gold-grad)" }}><Crown className="w-4 h-4 text-black" /></span>
              </div>
              <p className="font-display text-3xl mt-6 gold-text" data-testid="owner-name">{owner.name}</p>
              <p className="text-xs uppercase tracking-[0.25em] text-blue-100/70 mt-1" data-testid="owner-title">{owner.title}</p>
              <div className="flex flex-wrap justify-center gap-2 mt-4 text-xs">
                {owner.age && <span className="px-3 py-1 rounded-full border border-[#D4AF37]/40 text-blue-50" data-testid="owner-age">Age {owner.age}</span>}
                {owner.location && <span className="px-3 py-1 rounded-full border border-[#D4AF37]/40 text-blue-50 flex items-center gap-1" data-testid="owner-location"><MapPin className="w-3 h-3 text-[#D4AF37]" />{owner.location}</span>}
              </div>
              <a href={wa} target="_blank" rel="noopener noreferrer" className="mt-auto pt-6 w-full" data-testid="invest-whatsapp-btn">
                <span className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-[#25D366] hover:bg-[#1ebe5a] text-black font-semibold transition-colors"><MessageCircle className="w-4 h-4" />WhatsApp: +{owner.whatsapp}</span>
              </a>
            </div>
          </div>

          <div className="space-y-5">
            {owner.quote && (
              <div className="rounded-3xl border border-[#D4AF37]/30 bg-white/[0.03] backdrop-blur-xl p-7 relative rise" style={{ animationDelay: "200ms" }} data-testid="owner-quote">
                <Quote className="w-10 h-10 text-[#D4AF37]/40 absolute top-5 right-6" />
                <p className="font-display text-2xl sm:text-3xl italic leading-snug text-blue-50">“{owner.quote}”</p>
                <p className="text-sm gold-text mt-3">— {owner.name}</p>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-5">
              {[[Target, "Our goal", owner.goal, "owner-goal"], [Rocket, "Our mission", owner.mission, "owner-mission"]].map(([Icon, t, v, id], i) => (
                <div key={id} className="rounded-3xl border border-[#D4AF37]/25 bg-[#0d1f4a]/70 p-6 rise" style={{ animationDelay: `${280 + i * 80}ms` }} data-testid={id}>
                  <span className="w-10 h-10 rounded-full flex items-center justify-center mb-3" style={{ background: "var(--gold-grad)" }}><Icon className="w-4 h-4 text-black" /></span>
                  <p className="eyebrow !text-[10px] mb-2">{t}</p>
                  <p className="text-sm text-blue-50/90 leading-relaxed">{v}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-14">
          <p className="eyebrow !text-[10px] text-center mb-5">Why invest with us</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {WHY.map(([Icon, t, d]) => (
              <div key={t} className="rounded-2xl border border-white/10 hover:border-[#D4AF37]/60 bg-white/[0.03] p-5 transition-colors">
                <Icon className="w-6 h-6 text-[#D4AF37] mb-3" /><p className="font-semibold text-sm">{t}</p><p className="text-xs text-blue-100/70 mt-1">{d}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 rounded-3xl p-8 text-center border border-[#D4AF37]/40 bg-gradient-to-br from-[#0d1f4a] to-[#071330]">
          <h2 className="font-display text-3xl sm:text-4xl">Let's build it <span className="gold-text italic">together.</span></h2>
          <p className="text-blue-100/70 text-sm mt-2">To discuss investment, contact the founder directly. Message "INVEST".</p>
          <a href={wa} target="_blank" rel="noopener noreferrer" className="btn-gold mt-6" data-testid="invest-cta-btn"><MessageCircle className="w-4 h-4" />Contact Founder on WhatsApp</a>
        </div>
      </div>
      {edit && <OwnerEditor onClose={() => setEdit(false)} />}
    </div>
  );
}
